<template>
  <v-dialog v-model="dialog" max-width="600px">
    <v-card>
      <v-card-title>
        {{
          editing
            ? __("alterar código postal")
            : __("cadastro de código postal")
        }}

        <v-spacer></v-spacer>

        <v-btn @click="close" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <ValidationObserver
          ref="address_form"
          tag="div"
          class="mt-2"
          v-slot="{ invalid }"
        >
          <form @submit.prevent="handleSubmit" novalidate>
            <ValidationProvider
              name="address.postal_code"
              rules="required|min:4|max:10"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="__('CEP')"
                v-model="form.postal_code"
                @input="normalizePostalCode"
                :disabled="form_loading || form_submitting"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              name="address.district"
              rules="required"
              v-slot="{ errors }"
            >
              <select-country-area
                v-model="form.country_area"
                :label="__('Estado')"
                :disabled="form_loading || form_submitting"
                class="mt-2"
                name="country_area"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              name="address.country"
              rules="required"
              v-slot="{ errors }"
            >
              <select-city
                v-model="form.city"
                :label="__('Cidade')"
                :country_area="form.country_area"
                :disabled="form_loading || form_submitting"
                class="mt-2"
                name="city"
                :error-messages="errors"
              />
            </ValidationProvider>

            <ValidationProvider
              name="address.neighborhood"
              rules="required|max:15"
              v-slot="{ errors }"
            >
              <div class="d-flex align-center">
                <div style="flex: 1">
                  <select-city-area
                    ref="city_area_select"
                    v-model="form.city_area"
                    :label="__('Bairro')"
                    :city="form.city"
                    :disabled="form_loading || form_submitting"
                    class="mt-2"
                    name="city_area"
                    :error-messages="errors"
                  />
                </div>

                <v-btn
                  icon
                  small
                  class="ml-2 mt-2"
                  :disabled="!form.city"
                  @click="$refs.neighborhood_form.open(form.city?.id)"
                >
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
            </ValidationProvider>

            <ValidationProvider
              name="address.street"
              rules="required|min:4|max:255"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="__('Rua')"
                v-model="form.street"
                @blur="normalizeStreet"
                :disabled="form_loading || form_submitting"
                class="mt-2"
                name="street"
                :error-messages="errors"
              />
            </ValidationProvider>

            <v-row>
              <v-col cols="6">
                <ValidationProvider
                  name="address.latitude"
                  rules="required|min_value:-90|max_value:90"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    type="number"
                    :label="__('latitude')"
                    v-model="form.latitude"
                    @blur="normalizeCoordinate('latitude')"
                    :disabled="form_loading || form_submitting"
                    name="latitude"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="6">
                <ValidationProvider
                  name="address.longitude"
                  rules="required|min_value:-180|max_value:180"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    type="number"
                    :label="__('longitude')"
                    v-model="form.longitude"
                    :disabled="form_loading || form_submitting"
                    name="longitude"
                    @blur="normalizeCoordinate('longitude')"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                @click="close"
                text
                class="mt-4"
                :disabled="form_loading || form_submitting"
                >{{ __("cancelar") }}</v-btn
              >
              <v-btn
                type="submit"
                color="primary"
                class="mt-4"
                :loading="form_submitting"
                :disabled="form_submitting"
              >
                {{ editing ? __("salvar alterações") : __("Novo endereço") }}
              </v-btn>
            </v-card-actions>
          </form>
        </ValidationObserver>
      </v-card-text>
    </v-card>
    <NeighborhoodFormDialog ref="neighborhood_form" @submitted="refresh()" />
  </v-dialog>
</template>

<script>
import i18n from "@/mixins/i18n";
import ToastsMixin from "@/mixins/ToastsMixin.vue";
import SelectCountryArea from "@/components/locals/SelectCountryArea";
import SelectCity from "@/components/locals/SelectCity";
import SelectCityArea from "@/components/locals/SelectCityArea";
import {
  store as storeAddress,
  update as updateAddress,
} from "@/services/locals";
import NeighborhoodFormDialog from "@/components/PostalCodes/NeighborhoodFormDialog.vue";

export default {
  mixins: [i18n, ToastsMixin],
  name: "PostalCodeFormDialog",
  components: {
    SelectCountryArea,
    SelectCity,
    SelectCityArea,
    NeighborhoodFormDialog,
  },
  data() {
    return {
      i18n_prefix: "address",
      dialog: false,
      form: {
        postal_code: "",
        country_area: null,
        city: null,
        city_area: null,
        street: "",
        latitude: null,
        longitude: null,
      },
      form_submitting: false,
      form_loading: false,
      errors: [],
      editing: false,
      editedItem: null,
    };
  },
  methods: {
    open() {
      this.dialog = true;
      this.resetForm();
    },

    openForEdit(item) {
      this.editing = true;
      this.editedItem = item;

      this.dialog = true;
      this.form = {
        ...this.form,
        postal_code: item.postal_code || "",
        street: item.street || "",
        city: item.city || null,
        country_area: item.country_area || null,
        city_area: item.city_area || null,
        id: item.id || null,
        latitude: item.latitude || null,
        longitude: item.longitude || null,
      };
    },

    close() {
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.address_form?.reset();
      });

      this.dialog = false;
    },

    resetForm() {
      this.form = {
        postal_code: "",
        country_area: null,
        city: null,
        city_area: null,
        street: "",
        latitude: null,
        longitude: null,
      };
      this.editing = false;
      this.editedItem = null;
    },

    async handleCreate() {
      const isValid = await this.validateForm();
      if (!isValid) return;

      this.form_submitting = true;
      const payload = this.buildPayload();

      try {
        const response = await storeAddress(payload);
        this.$emit("submitted", response);
        this.close();
        this.toast(this.__("endereço registado com sucesso", "address"));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.form_submitting = false;
      }
    },

    async handleUpdate() {
      const isValid = await this.validateForm();
      if (!isValid) return;

      this.form_submitting = true;
      const payload = this.buildPayload();

      try {
        const response = await updateAddress(this.form.id, payload);
        this.$emit("submitted", response);
        this.close();
        this.toast(this.__("tipo atualizado com sucesso", "settings"));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.form_submitting = false;
      }
    },

    async validateForm() {
      const isValid = await this.$refs.address_form.validate();
      if (!isValid) {
        this.toastError(
          this.__("preencha os campos obrigatórios corretamente", "address")
        );
      }
      return isValid;
    },

    buildPayload() {
      return {
        postal_code: this.form.postal_code.trim().replace(/\D/g, ""),
        city_id: this.form.city?.id,
        country_area_id: this.form.country_area?.id,
        city_area_id: this.form.city_area?.id,
        street: this.form.street?.trim() || "",
        latitude: parseFloat(this.form.latitude),
        longitude: parseFloat(this.form.longitude),
      };
    },

    handleError(error) {
      console.error(error);

      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 400 && data?.message) {
        this.toastError(data.message);
        return;
      }

      if (status === 422 && data?.errors) {
        const messages = Object.values(data.errors).flat().join("\n");
        this.toastError(messages);
        return;
      }

      this.toastError(this.__("Não foi possível registar", "settings"));
    },

    handleSubmit() {
      if (this.form.id) {
        this.handleUpdate();
      } else {
        this.handleCreate();
      }
    },

    normalizePostalCode() {
      this.form.postal_code = this.form.postal_code.trim().replace(/\D/g, "");
    },

    normalizeStreet() {
      if (this.form.street) {
        this.form.street = this.form.street.trim();
      }
    },
    normalizeCoordinate(field) {
      const value = this.form[field];
      if (typeof value === "string") {
        this.form[field] = value.replace(",", ".");
      }
    },

    refresh() {
      if (this.form.city?.id) {
        this.$refs.city_area_select?.refresh(this.form.city);
      }
    },
  },
};
</script>
